import { useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { moneyFormatter } from "@atlas-int/data";
import styled from "@emotion/styled";
import {
  dataVizTypography,
  surface,
  border as borderToken,
  shadow,
  spacing,
  fontSize as fontSizeTokens,
  zIndex as zIndexTokens,
  borderRadius,
} from "@atlas-int/tokens";

interface TotalValueProps {
  value: number | undefined;
  year?: number;
  isOvertime?: boolean;
}

const TooltipCard = styled.div`
  position: fixed;
  background-color: ${surface.white};
  box-shadow: ${shadow.sm};
  padding: ${spacing[4]};
  font-size: ${fontSizeTokens.xs};
  border-radius: ${borderRadius.base};
  border: 1px solid ${borderToken.subtle};
  white-space: normal;
  width: max-content;
  max-width: 280px;
  z-index: ${zIndexTokens.overlay};
  pointer-events: none;
`;

const Wrapper = styled.div`
  display: inline-block;
`;

/**
 * TotalValue for the library package.
 *
 * Unlike the app version, this does not use usePageQueryParams or useLocation.
 * Instead it accepts year and isOvertime as props.
 */
const TotalValue = ({ value, year, isOvertime }: TotalValueProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }, []);

  const handleEnter = useCallback(() => {
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const handleLeave = useCallback(() => {
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, updatePosition]);

  if (!value) {
    return <div style={{ width: 100 }} />;
  }

  return (
    <Wrapper>
      <div
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{
          fontSize: dataVizTypography.totalValueSize,
          fontWeight: dataVizTypography.totalValueWeight,
          borderBottom: "1px dashed",
          cursor: "help",
          whiteSpace: "nowrap",
        }}
      >
        Total Value: ${moneyFormatter.format(value)}{" "}
        {isOvertime && year && `(${year})`}
      </div>
      {visible &&
        createPortal(
          <TooltipCard
            style={{
              top: pos.top,
              left: pos.left,
              transform: "translateY(-50%)",
            }}
          >
            Total Trade Value: Represents the sum of all trade data currently
            displayed. This value dynamically updates based on your selected
            adjustments in the visualization.
          </TooltipCard>,
          document.body,
        )}
    </Wrapper>
  );
};

export default TotalValue;
